import CardPreview from "@cardRendering/cardPreview";
import ClickableLabeledSection from "@components/clickableLabeledSection";
import Empty from "@components/empty";
import { IonList, IonProgressBar } from "@ionic/react";
import { Deck, IDeck } from "@models/deck";
import { IKnol, Knol } from "@models/knol";
import { MoreButton } from "@screens/deck/cardsSection";
import EventBus from "eventBus";
import { superCache } from "hooks/data/superCache";
import { ID } from "types/ID";

import { useEffect, useState } from "react";

const NUM_MAX_CARD_PREVIEWS_PER_DECK = 10;

function cardMarginPx() {
  const x = 4;
  if (window.innerWidth > 600) {
    return x * 1;
  }
  return x;
}

function optimalCardWidth(
  containerWidthPx: number,
  minCardWidthPx: number | null = null,
  borderPx: number | null = null,
) {
  const baseMinWidth = minCardWidthPx || 148;
  const border = borderPx || 1;

  const minWidth = containerWidthPx > 600 ? baseMinWidth * 1.7 : baseMinWidth;

  const marginPx = cardMarginPx();

  const extraWidthPx = marginPx * 2 + border * 2;
  const effectiveContainerWidthPx = containerWidthPx - marginPx;
  const maxCardsPerRow = Math.floor(effectiveContainerWidthPx / (minWidth + extraWidthPx));
  const w = Math.floor(effectiveContainerWidthPx / maxCardsPerRow);
  return w - extraWidthPx;
}

function cardWidthPx() {
  return optimalCardWidth(window.innerWidth);
}

function cardHeightPx() {
  return Math.round(cardWidthPx() * 1.2);
}

export interface ILayoutResult {
  id: string;
  name: string;
  style: string;
  templatesJson: string;
  templates: string[];
}

function DeckOfCards(props: {
  deck: IDeck;
  cards: IKnol[];
  query: string;
}): JSX.Element {
  const { deck, cards, query } = props;

  const cardHeight = cardHeightPx();
  const cardWidth = cardWidthPx();
  const cardMargin = cardMarginPx();
  const displayName = Deck.displayName(deck);

  return (
    <ClickableLabeledSection label={displayName} href={`/decks/local/${deck.id}`}>
      <div
        style={{
          overflowX: "auto",
          display: "flex",
          flexDirection: "row",
          textAlign: "center",
          padding: 8,
          gap: 8,
        }}
      >
        {cards.slice(0, NUM_MAX_CARD_PREVIEWS_PER_DECK).map((card) => {
          return (
            <CardPreview
              key={card.id}
              isClickable
              showHeader
              query={query}
              visibleSide={1}
              deck={deck}
              knol={card}
              cardWidth={cardWidth}
              cardHeight={cardHeight}
              disableAudioControlsRendering
            />
          );
        })}

        {cards.length > NUM_MAX_CARD_PREVIEWS_PER_DECK && (
          <MoreButton
            deckID={deck.id}
            query={query}
            cardWidth={cardWidth}
            cardHeight={cardHeight}
            cardMargin={cardMargin}
          />
        )}
      </div>
    </ClickableLabeledSection>
  );
}

export default function CardResults(props: {
  query: string;
}) {
  const { query } = props;

  const [state, setState] = useState({
    loading: query !== "",
    results: new Map() as Map<ID, { deck: IDeck; knolsWithGrades: IKnol[] }>,
  });

  useEffect(() => {
    const fetchCards = async (
      query: string,
    ): Promise<Map<ID, { deck: IDeck; knolsWithGrades: IKnol[] }>> => {
      const knols = [] as IKnol[];
      const deckIds = new Set<ID>();

      // Iterate through all knols in the cache
      for (const knol of superCache.knols?.values() ?? []) {
        if (Knol.matchesQuery(knol, query)) {
          knols.push(knol);
          deckIds.add(knol.deck_id);
        }
      }

      const knolsWithGrades = knols.map((knol) => ({
        ...knol,
        grades: knol.grades || {}, // Ensure grades is an object, even if empty
      }));

      const resultMap = new Map<ID, { deck: IDeck; knolsWithGrades: IKnol[] }>();

      for (const deckId of deckIds) {
        const deck = superCache.decks?.get(deckId);
        if (!deck) continue; // Skip if deck is not found

        // Skip archived decks
        if (Deck.isArchived(deck)) continue;

        const filteredKnolsWithGrades = knolsWithGrades.filter((knol) => knol.deck_id === deckId);
        resultMap.set(deckId, { deck, knolsWithGrades: filteredKnolsWithGrades });
      }

      return resultMap;
    };

    const handleUpdate = () => {
      if (query) {
        fetchCards(query)
          .then((r) => {
            setState({ loading: false, results: r });
          })
          .catch((err) => {
            setState((prevState) => ({ ...prevState, loading: false }));
          });
      } else {
        setState({
          results: new Map() as Map<ID, { deck: IDeck; knolsWithGrades: IKnol[] }>,
          loading: false,
        });
      }
    };

    handleUpdate();
    EventBus.on("knolUpdated", handleUpdate);
    return () => {
      EventBus.off("knolUpdated", handleUpdate);
    };
  }, [query]);

  const items = state.results
    ? [...state.results.entries()].map(([deckId, { deck, knolsWithGrades }]) => {
        return <DeckOfCards key={deckId} deck={deck} cards={knolsWithGrades} query={query} />;
      })
    : [];

  return (
    <IonList>
      {state.loading && <IonProgressBar type="indeterminate" />}
      {items.length < 1 && !state.loading && <Empty />}
      {items}
    </IonList>
  );
}
